import React, { memo } from 'react';
import { Release } from '../types';
import { useReleases } from '../context/ReleasesContext';

interface ExploreReleaseCardProps {
  release: Release;
  labelId: number;
  labelName: string;
  isChecked: boolean;
  onToggleCheck: (releaseId: number) => void;
  onTagClick?: (tag: string) => void;
  selectedGenres: string[];
}

const ExploreReleaseCard: React.FC<ExploreReleaseCardProps> = memo(({
  release,
  labelId,
  labelName,
  isChecked,
  onToggleCheck,
  onTagClick,
  selectedGenres
}) => {
  const { loadingReleaseId } = useReleases();
  const isLoading = !!(release.id && loadingReleaseId === release.id);
  const genreCount = release.genres?.length || 0;

  return (
    <div className="rounded-xl w-full shadow-md hover:shadow-xl transition-shadow duration-300 flex h-[320px] mb-8">
      {/* Left side: Image and info in a vertical layout */}
      <a
        href={release.release_url}
        target="_blank"
        rel="noopener noreferrer"
        className="w-48 min-w-[12rem] flex-shrink-0 flex flex-col group cursor-pointer overflow-hidden rounded-l-xl"
      >
        {/* Image container */}
        <div className="relative">
          <img
            src={release.image_url!}
            alt={`${release.title} by ${release.artist}`}
            className="w-48 h-48 object-cover"
            loading="lazy"
          />
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleCheck(release.id!);
            }}
            disabled={isLoading}
            className={`absolute bottom-3 right-3 p-2.5 rounded-full shadow-lg ${
              isChecked ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-800/80 hover:bg-gray-700/80'
            } text-white transition-all duration-200 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : isChecked ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        </div>

        {/* Info section with improved spacing and typography */}
        <div className="p-5 bg-gray-50 flex-shrink-0 flex-grow flex flex-col">
          <h3 className="font-bold text-base text-gray-900 truncate mb-2" title={release.title}>
            {release.title}
          </h3>
          <p className="text-sm text-gray-700 font-medium truncate mb-2" title={release.artist}>
            {release.artist}
          </p>
          <p className="text-xs text-gray-600 truncate mb-1" title={labelName}>
            {labelName}
          </p>
          <p className="text-xs text-gray-500 truncate mt-auto" title={release.release_date}>
            {release.release_date}
          </p>
        </div>
      </a>

      {/* Right side: Scrollable genre tags with background and scroll indicator */}
      <div className="flex-grow overflow-hidden min-w-0 bg-violet-50/30 relative rounded-r-xl">
        <div className="h-full overflow-y-auto custom-scrollbar p-3">
          <div className="flex flex-wrap gap-2 content-start">
            {release.genres?.map((genre, index) => {
              const isSelected = selectedGenres.includes(genre);
              return (
                <span
                  key={`${genre}-${index}`}
                  onClick={onTagClick ? () => onTagClick(genre) : undefined}
                  role="button"
                  tabIndex={0}
                  className={`py-1 px-2.5 text-xs rounded-md transition-all duration-150 whitespace-nowrap !inline-block
                    ${isSelected
                      ? 'bg-violet-500 text-white font-semibold border border-violet-400 shadow-sm'
                      : 'bg-violet-100 text-gray-700 border border-violet-200 hover:bg-violet-200 hover:border-violet-300'
                    } cursor-pointer`}
                >
                  {genre}
                </span>
              );
            })}
          </div>
        </div>

        {/* Bottom fade gradient to indicate scrollable content */}
        {genreCount > 0 && (
          <div
            className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(237, 233, 254, 0.95) 0%, rgba(237, 233, 254, 0.7) 30%, rgba(237, 233, 254, 0.3) 60%, transparent 100%)'
            }}
          />
        )}
      </div>
    </div>
  );
});

ExploreReleaseCard.displayName = 'ExploreReleaseCard';

export default ExploreReleaseCard; 